/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { Button, Dropdown, FloatingLabel, Form, InputGroup, Modal, OverlayTrigger, Popover, Spinner} from "react-bootstrap";
import { useEffect, useReducer, useState } from "react";
import Address from "../../models/Address.js";
import CustomButton from "../CustomButton.js";
import img1 from '../../assets/addressInstructions1.png';
import img2 from '../../assets/addressInstructions2.png';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { AppGlobals } from "../../App.tsx";
import { autoCompleteAddress, validateAddress } from "../../services/AddressService.js";
import { addressToString } from "../../tools.tsx";

let timestamp = 0;

export default function AddressAutoFill({label, stateProps, noRedAsterisk}){
    const [isSearching, setIsSearching] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [search, setSearch] = useState('');
    const [lastSearch, setLastSearch]= useState('');
    const [showDropdown, setShowDropdown] = useState(true);
    const [searchResults, setSearchResults] = useState([]);
    const [ticker, setTicker] = useState(0);
    const [showValidation, setShowValidation] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [_, forceUpdate] = useReducer(x => x + 1, 0);

    useEffect(() => {
        stateProps?.subscribe((showValidation) => {
            if (showValidation) {
                setShowValidation(true);
            }
            forceUpdate();
        });
    }, [stateProps]);

    useEffect(() => {
        if (search !== lastSearch && search) {
            setLastSearch(search);
            handleSearchRequest();
        }
    }, [ticker])

    useEffect(() => {
        const interval = setInterval(handleInterval, 1000);
        return () => clearInterval(interval);
    }, []);

    async function handleSearchRequest(){
        setIsSearching(true);
        const response = await autoCompleteAddress(search);
        if (response.status === 200) {
            setSearchResults(response.predictions.map(p => { return {description: p.description, placeId: p.place_id}}));
        }
        setIsSearching(false);
    }

    async function handleValidateAddress(placeId){
        setShowDropdown(false);
        setIsValidating(true);
        const response = await validateAddress(placeId);
        if (response.status === 200) {
            stateProps.setValue(Address.decodeValidated(response.address));
            if (!showValidation) {
                setShowValidation(true);
            }
        } else {
            AppGlobals.alert('danger', response.message);
        }
        setIsValidating(false);
    }

    function handleInterval(){
        setTicker(Date.now());
    }

    function handleSetValue(value){
        setSearch(value);
    }

    const searchResultElements = searchResults.map((sr) => {
        return (
            <Dropdown.Item key={sr.placeId} onClick={() => {setSearch(sr.description); handleValidateAddress(sr.placeId)}}>{sr.description}</Dropdown.Item>
        )
    })

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'center', gap: 4}}>
                <InputGroup>
                {stateProps.getValue()?.checkIfValid() && <CustomButton onClick={() => stateProps.setValue(undefined)} isLoading={isValidating}>Edit</CustomButton>}
                    { stateProps.getValue()?.checkIfValid() ?
                        <OverlayTrigger overlay={(
                            <Popover style={{position: 'fixed'}}>
                                <Popover.Body>
                                    {addressToString(stateProps.getValue())}
                                </Popover.Body>
                            </Popover>
                        )}>
                            <Form.Control isValid={showValidation && stateProps?.getIsValid()} style={{height: 58, flex: 1}} value={addressToString(stateProps.getValue())} readOnly disabled/>
                        </OverlayTrigger>
                    :
                        <FloatingLabel label={<div>{label}<span style={{color: 'red'}}>{(stateProps?.hasValidation() && !noRedAsterisk) ? '*' : ''}</span></div>}>
                            <Form.Control 
                                value={search}
                                style={{height: 58}}
                                onFocus={() => {setShowDropdown(true)}}
                                onChange={(e)=>handleSetValue(e.target.value)}
                                placeholder=" "
                                disabled={isValidating}
                                isInvalid={showValidation && stateProps?.getIsInvalid()}
                            />
                        </FloatingLabel>
                    }

                    <CustomButton variant={'primary'} style={{color: 'white'}} isLoading={isValidating} onClick={()=>{setSearch(''); stateProps.setValue(undefined)}}>Clear</CustomButton>
                </InputGroup>
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <FontAwesomeIcon style={{width: 24, height: 24, color: 'var(--bs-primary)', cursor: 'pointer'}} onClick={() => setShowModal(true)} icon={faCircleQuestion}/>
                </div>
            </div>
            <Dropdown show={searchResults.length > 0 && search && showDropdown} onBlur={()=>setShowDropdown(false)}>
                <Dropdown.Menu style={{width: '100%'}}>
                    { isSearching 
                        ? <Dropdown.Item disabled style={{display: 'flex', justifyContent: 'center'}}>
                            <Spinner style={{color: 'var(--bs-primary)', padding: 12}}/> 
                          </Dropdown.Item>
                        : searchResultElements
                    }
                </Dropdown.Menu>
            </Dropdown>
            <Modal size="lg" centered show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Address Help</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>To add a secondary street address into the form, type both the prefix (ex: apt) and suffix (ex: 102) after the primary street address.</p>
                    <img style={{ width: '100%', padding: 16}} src={img1}/>
                    <img style={{ width: '100%', padding: 16}} src={img2}/>
                </Modal.Body>
            </Modal>
        </div>
    )
}